import { useEffect, useState } from "react";
import { getMaritalStatus } from "../../api/fundsCalculatorApi";

export const Step1Form = ({ formData, setFormData, handleInputChange }) => {
  const [maritalStatusList, setMaritalStatusList] = useState([]);

  //  Fetch marital status list on mount
  useEffect(() => {
    const fetchMaritalStatus = async () => {
      try {
        const response = await getMaritalStatus();
        const data = response.data.data || [];
        setMaritalStatusList(data);

        //  Auto-select "Single" if available & nothing selected yet
        const singleOption = data.find(
          (item) => item.maritial_type.toLowerCase() === "single",
        );

        if (singleOption && !formData.maritalStatus) {
          setFormData((prev) => ({
            ...prev,
            maritalStatus: singleOption.id.toString(),
          }));
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchMaritalStatus();
  }, []);

  const selectedStatus = maritalStatusList.find(
    (item) => Number(item.id) === Number(formData.maritalStatus),
  );

  const maritalType = selectedStatus?.maritial_type?.toLowerCase?.();

  const hideSpouse = ["single", "divorced", "widow"].includes(maritalType);
  const hideKids = maritalType === "single";

  useEffect(() => {
    if (hideSpouse) {
      setFormData((prev) => ({
        ...prev,
        spouseAccompany: "",
        isSingle: true, // means "no spouse required"
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        isSingle: false,
      }));
    }
  }, [hideSpouse]);

  useEffect(() => {
    if (maritalType === "single") {
      setFormData((prev) => ({
        ...prev,
        spouseAccompany: "",
        takingKids: "",
        childrenCount: 1,
        childrenAges: [""],
        isSingle: true,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        isSingle: false,
      }));
    }
  }, [maritalType]);

  const handleMaritalChange = (value) => {
    const selected = maritalStatusList.find(
      (item) => item.id.toString() === value,
    );

    const maritalType = selected?.maritial_type?.toLowerCase?.();

    const hideSpouse = ["single", "divorced", "widow"].includes(maritalType);

    setFormData((prev) => ({
      ...prev,
      maritalStatus: value,
      isSingle: hideSpouse, // ✅ correctly defined
      spouseAccompany: "",
      takingKids: "",
      childrenCount: 1,
      childrenAges: [""],
    }));
  };

  return (
    <>
      <div className="grid grid-cols-3 gap-6 mb-6">
        {/* Marital Status */}
        <div>
          <label className="block text-black text-[16px] font-altis mb-2">
            Marital Status <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.maritalStatus}
            onChange={(e) => handleMaritalChange(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#572575] focus:border-transparent"
          >
            <option value="">Select marital status</option>
            {maritalStatusList.map((item) => (
              <option key={item.id} value={item.id.toString()}>
                {item.maritial_type}
              </option>
            ))}
          </select>
        </div>

        {/* Conditionally show spouse & kids sections */}
        {/* Spouse Accompany (conditionally hidden) */}
        {!hideSpouse && (
          <div>
            <label className="block text-black text-[16px] font-altis mb-4">
              Will your spouse accompany you?{" "}
              <span className="text-red-500">*</span>
            </label>
            <div className="flex space-x-6">
              {["Yes", "No"].map((option) => (
                <label
                  key={option}
                  className="flex items-center cursor-pointer"
                >
                  <input
                    type="radio"
                    name="spouseAccompany"
                    value={option}
                    checked={formData.spouseAccompany === option}
                    onChange={(e) =>
                      handleInputChange("spouseAccompany", e.target.value)
                    }
                    className="w-4 h-4 accent-[#9b228e]"
                  />
                  <span className="ml-2 text-black text-[16px]">{option}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Taking Kids (conditionally hidden for single/divorced/widow) */}
        {!hideKids && (
          <div>
            <label className="block text-black text-[16px] font-altis mb-4">
              Are you taking your kids along?{" "}
              <span className="text-red-500">*</span>
            </label>
            <div className="flex space-x-6">
              {["Yes", "No"].map((option) => (
                <label
                  key={option}
                  className="flex items-center cursor-pointer"
                >
                  <input
                    type="radio"
                    name="takingKids"
                    value={option}
                    checked={formData.takingKids === option}
                    onChange={(e) =>
                      handleInputChange("takingKids", e.target.value)
                    }
                    className="w-4 h-4 accent-[#9b228e]"
                  />
                  <span className="ml-2 text-black text-[16px]">{option}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Children Section */}
      {formData.takingKids === "Yes" && (
        <div className="grid grid-cols-3 gap-6 mb-6 mt-4">
          <div>
            <label className="block text-black text-[16px] font-altis mb-2">
              How many children do you have?{" "}
              <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.childrenCount}
              onChange={(e) =>
                handleInputChange("childrenCount", e.target.value)
              }
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#572575] focus:border-transparent"
            >
              <option value="">Select Number of children</option>
              {[1, 2, 3, 4].map((n) => (
                <option key={n} value={n}>
                  {n} {n === 1 ? "Child" : "Children"}
                </option>
              ))}
            </select>
          </div>

          {/* Child Age Inputs */}
          {formData.childrenAges.map((age, index) => (
            <div key={index}>
              <label className="block text-black text-[16px] font-altis mb-2">
                Child {index + 1} Age <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                placeholder="5"
                min="0"
                max="18"
                value={age}
                onChange={(e) => {
                  const val = e.target.value;
                  // Allow only numbers and ensure age ≤ 18
                  if (/^\d*$/.test(val) && (val === "" || Number(val) <= 18)) {
                    handleInputChange("childrenAges", val, index);
                  }
                }}
                onBlur={(e) => {
                  const val = Number(e.target.value);
                  if (val > 18) {
                    alert("Child's age cannot exceed 18 years.");
                    handleInputChange("childrenAges", "18", index);
                  }
                }}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#572575] focus:border-transparent"
                inputMode="numeric"
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
};
